/**
 * ConditionalFormattingPopover — authoring UI for cell rules.
 *
 * Holds a working copy of the rule list while open; commits to the
 * controller via `setCellsConditionalFormatting` only on Apply. Cancel
 * (or click-outside / Escape) discards changes.
 *
 * Selection-uniformity policy: when the caller passes `isMixed`, the
 * popover starts with an empty rule list and shows a "Mixed selection
 * — apply will overwrite" notice; on Apply the same fresh list is
 * written to every selected cell.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Plus, RotateCcw, Trash2, X } from 'lucide-react';

import {
  ThemedSelect,
  type ThemedSelectOption,
} from '../../../components/common/ThemedSelect';
import {
  CF_OPS,
  MAX_CF_AND_CONDITIONS_PER_GROUP,
  MAX_CF_OR_GROUPS_PER_RULE,
  MAX_CF_RULES_PER_CELL,
  type CfCondition,
  type CfOp,
  type CfRule,
} from './conditionalFormatting';
import styles from './ConditionalFormattingPopover.module.css';

const OP_OPTIONS: ReadonlyArray<ThemedSelectOption<CfOp>> = CF_OPS.map((op) => ({
  value: op,
  label: op,
}));

const COLOR_PALETTE: readonly string[] = [
  '#ffffff', '#d1d5db', '#9ca3af', '#6b7280', '#374151', '#000000',
  '#dc2626', '#ea580c', '#d97706', '#65a30d', '#2563eb', '#7c3aed',
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#3b82f6', '#a78bfa',
  '#fca5a5', '#fdba74', '#fcd34d', '#bef264', '#93c5fd', '#c4b5fd',
  '#fee2e2', '#ffedd5', '#fef3c7', '#ecfccb', '#dbeafe', '#ede9fe',
];

const NEXT_PALETTE = [
  '#dc2626',
  '#65a30d',
  '#2563eb',
  '#d97706',
  '#7c3aed',
  '#0ea5e9',
];

const newRuleId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `cf-${crypto.randomUUID()}`;
  }
  return `cf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

const cloneRule = (rule: CfRule): CfRule => ({
  id: rule.id,
  groups: rule.groups.map((g) => g.map((c) => ({ op: c.op, target: c.target }))),
  textColor: rule.textColor,
  bgColor: rule.bgColor,
});

const cloneRules = (rules: readonly CfRule[]): CfRule[] => rules.map(cloneRule);

interface DraftCondition {
  op: CfOp;
  /** Verbatim user text. Empty string means "needs input". */
  rawTarget: string;
}

interface DraftRule {
  id: string;
  groups: DraftCondition[][];
  textColor?: string;
  bgColor?: string;
}

const conditionToDraft = (cond: CfCondition): DraftCondition => ({
  op: cond.op,
  rawTarget: Number.isFinite(cond.target) ? String(cond.target) : '',
});

const ruleToDraft = (rule: CfRule): DraftRule => ({
  id: rule.id,
  groups: rule.groups.map((g) => g.map(conditionToDraft)),
  textColor: rule.textColor,
  bgColor: rule.bgColor,
});

const parseTarget = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

const draftToRule = (draft: DraftRule): CfRule | null => {
  if (!draft.textColor && !draft.bgColor) return null;
  const groups: CfCondition[][] = [];
  for (const draftGroup of draft.groups) {
    if (draftGroup.length === 0) return null;
    const group: CfCondition[] = [];
    for (const dc of draftGroup) {
      const t = parseTarget(dc.rawTarget);
      if (t === null) return null;
      group.push({ op: dc.op, target: t });
    }
    groups.push(group);
  }
  const out: CfRule = { id: draft.id, groups };
  if (draft.textColor) out.textColor = draft.textColor;
  if (draft.bgColor) out.bgColor = draft.bgColor;
  return out;
};

const draftsToRules = (drafts: DraftRule[]): { rules: CfRule[] | null; firstError: number } => {
  const out: CfRule[] = [];
  for (let i = 0; i < drafts.length; i += 1) {
    const r = draftToRule(drafts[i]);
    if (!r) return { rules: null, firstError: i };
    out.push(r);
  }
  return { rules: out, firstError: -1 };
};

const ruleErrorReason = (draft: DraftRule): string | null => {
  if (!draft.textColor && !draft.bgColor) {
    return 'Choose a text or fill color.';
  }
  for (let g = 0; g < draft.groups.length; g += 1) {
    const group = draft.groups[g];
    if (group.length === 0) {
      return `Group ${g + 1} is empty. Add a condition or remove the group.`;
    }
    for (let k = 0; k < group.length; k += 1) {
      if (parseTarget(group[k].rawTarget) === null) {
        return `Enter a number for group ${g + 1}, condition ${k + 1}.`;
      }
    }
  }
  return null;
};

export interface ConditionalFormattingPopoverProps {
  anchor: { x: number; y: number };
  initialRules: CfRule[];
  isMixed: boolean;
  onApply: (rules: CfRule[]) => void;
  onCancel: () => void;
}

export const ConditionalFormattingPopover: React.FC<ConditionalFormattingPopoverProps> = ({
  anchor,
  initialRules,
  isMixed,
  onApply,
  onCancel,
}) => {
  const [drafts, setDrafts] = useState<DraftRule[]>(() =>
    isMixed ? [] : cloneRules(initialRules).map(ruleToDraft),
  );
  const [openColorPicker, setOpenColorPicker] = useState<{
    ruleIdx: number;
    kind: 'text' | 'bg';
  } | null>(null);

  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [resolvedPos, setResolvedPos] = useState<{ x: number; y: number }>(
    anchor,
  );

  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const margin = 8;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = anchor.x;
    let y = anchor.y;
    if (x + rect.width > vw - margin) x = Math.max(margin, vw - rect.width - margin);
    if (y + rect.height > vh - margin) y = Math.max(margin, vh - rect.height - margin);
    if (x !== resolvedPos.x || y !== resolvedPos.y) setResolvedPos({ x, y });
  }, [anchor, resolvedPos.x, resolvedPos.y]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();
      }
    };
    const onClick = (event: MouseEvent): void => {
      if (popoverRef.current && popoverRef.current.contains(event.target as Node)) return;
      onCancel();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [onCancel]);

  const addRule = useCallback(() => {
    setDrafts((prev) => {
      if (prev.length >= MAX_CF_RULES_PER_CELL) return prev;
      const seedColor = NEXT_PALETTE[prev.length % NEXT_PALETTE.length];
      const next: DraftRule = {
        id: newRuleId(),
        groups: [[{ op: '>', rawTarget: '0' }]],
        textColor: '#ffffff',
        bgColor: seedColor,
      };
      return [...prev, next];
    });
  }, []);

  const removeRule = useCallback((idx: number) => {
    setDrafts((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const setRuleColor = useCallback(
    (idx: number, kind: 'text' | 'bg', color: string | null) => {
      setDrafts((prev) =>
        prev.map((r, i) => {
          if (i !== idx) return r;
          if (kind === 'text') {
            const next = { ...r };
            if (color === null) delete next.textColor;
            else next.textColor = color;
            return next;
          }
          const next = { ...r };
          if (color === null) delete next.bgColor;
          else next.bgColor = color;
          return next;
        }),
      );
      setOpenColorPicker(null);
    },
    [],
  );

  const addOrGroup = useCallback((idx: number) => {
    setDrafts((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        if (r.groups.length >= MAX_CF_OR_GROUPS_PER_RULE) return r;
        return {
          ...r,
          groups: [...r.groups, [{ op: '>', rawTarget: '0' }]],
        };
      }),
    );
  }, []);

  const removeOrGroup = useCallback((idx: number, gIdx: number) => {
    setDrafts((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        return { ...r, groups: r.groups.filter((_, gg) => gg !== gIdx) };
      }),
    );
  }, []);

  const addAndCondition = useCallback((idx: number, gIdx: number) => {
    setDrafts((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        return {
          ...r,
          groups: r.groups.map((g, gg) => {
            if (gg !== gIdx) return g;
            if (g.length >= MAX_CF_AND_CONDITIONS_PER_GROUP) return g;
            return [...g, { op: '>', rawTarget: '0' }];
          }),
        };
      }),
    );
  }, []);

  const removeAndCondition = useCallback(
    (idx: number, gIdx: number, kIdx: number) => {
      setDrafts((prev) =>
        prev.map((r, i) => {
          if (i !== idx) return r;
          return {
            ...r,
            groups: r.groups.map((g, gg) =>
              gg === gIdx ? g.filter((_, kk) => kk !== kIdx) : g,
            ),
          };
        }),
      );
    },
    [],
  );

  const updateCondition = useCallback(
    (
      idx: number,
      gIdx: number,
      kIdx: number,
      patch: Partial<DraftCondition>,
    ) => {
      setDrafts((prev) =>
        prev.map((r, i) => {
          if (i !== idx) return r;
          return {
            ...r,
            groups: r.groups.map((g, gg) => {
              if (gg !== gIdx) return g;
              return g.map((c, kk) => (kk === kIdx ? { ...c, ...patch } : c));
            }),
          };
        }),
      );
    },
    [],
  );

  const errors = useMemo(() => drafts.map(ruleErrorReason), [drafts]);
  const firstError = errors.findIndex((e) => e !== null);
  const canApply = drafts.length === 0 || firstError === -1;

  const handleApply = useCallback(() => {
    if (!canApply) return;
    const built = draftsToRules(drafts);
    if (!built.rules) return;
    onApply(built.rules);
  }, [canApply, drafts, onApply]);

  return (
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{ top: resolvedPos.y, left: resolvedPos.x }}
      role="dialog"
      aria-label="Conditional formatting"
      data-testid="cf-popover"
    >
      <div className={styles.header}>
        <h3 className={styles.title}>Conditional formatting</h3>
        <span className={styles.headerNote}>
          {drafts.length} / {MAX_CF_RULES_PER_CELL}
        </span>
      </div>

      <div className={styles.body}>
        {isMixed ? (
          <div className={styles.mixedBar} data-testid="cf-mixed-banner">
            <span>
              Mixed selection — applying will overwrite each cell with the rule
              list below.
            </span>
          </div>
        ) : null}

        {drafts.length === 0 && !isMixed ? (
          <div className={styles.empty}>
            No rules. Click <em>Add rule</em> below.
          </div>
        ) : null}

        {drafts.map((rule, idx) => {
          const errorMessage = errors[idx];
          return (
            <div
              key={rule.id}
              className={styles.ruleCard}
              role="group"
              aria-labelledby={`cf-rule-${idx}-label`}
              data-testid={`cf-rule-${idx}`}
            >
              <div className={styles.ruleHeader}>
                <span
                  id={`cf-rule-${idx}-label`}
                  className={styles.ruleHeaderLabel}
                >
                  Rule {idx + 1}
                </span>
                <div className={styles.colorPickerWrap}>
                  <span className={styles.paletteLabel}>Text</span>
                  <button
                    type="button"
                    className={styles.colorChip}
                    style={{
                      backgroundColor: rule.textColor ?? 'transparent',
                      borderColor: rule.textColor ? rule.textColor : undefined,
                    }}
                    aria-label="Choose text color"
                    onClick={() =>
                      setOpenColorPicker((prev) =>
                        prev && prev.ruleIdx === idx && prev.kind === 'text'
                          ? null
                          : { ruleIdx: idx, kind: 'text' },
                      )
                    }
                    data-testid={`cf-rule-${idx}-text-color-trigger`}
                  />
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => setRuleColor(idx, 'text', null)}
                    disabled={!rule.textColor}
                    aria-label="Clear text color"
                    data-testid={`cf-rule-${idx}-text-color-clear`}
                  >
                    <RotateCcw size={14} aria-hidden="true" />
                  </button>
                </div>
                <div className={styles.colorPickerWrap}>
                  <span className={styles.paletteLabel}>Fill</span>
                  <button
                    type="button"
                    className={styles.colorChip}
                    style={{
                      backgroundColor: rule.bgColor ?? 'transparent',
                      borderColor: rule.bgColor ? rule.bgColor : undefined,
                    }}
                    aria-label="Choose fill color"
                    onClick={() =>
                      setOpenColorPicker((prev) =>
                        prev && prev.ruleIdx === idx && prev.kind === 'bg'
                          ? null
                          : { ruleIdx: idx, kind: 'bg' },
                      )
                    }
                    data-testid={`cf-rule-${idx}-bg-color-trigger`}
                  />
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => setRuleColor(idx, 'bg', null)}
                    disabled={!rule.bgColor}
                    aria-label="Clear fill color"
                    data-testid={`cf-rule-${idx}-bg-color-clear`}
                  >
                    <RotateCcw size={14} aria-hidden="true" />
                  </button>
                </div>
                <button
                  type="button"
                  className={`${styles.iconButton} ${styles.deleteSpacer}`}
                  onClick={() => removeRule(idx)}
                  aria-label={`Delete rule ${idx + 1}`}
                  data-testid={`cf-rule-${idx}-delete`}
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              </div>

              {openColorPicker &&
              openColorPicker.ruleIdx === idx &&
              openColorPicker.kind === 'text' ? (
                <div
                  className={styles.palette}
                  role="group"
                  aria-label="Text color palette"
                  data-testid={`cf-rule-${idx}-text-color-palette`}
                >
                  {COLOR_PALETTE.map((color) => (
                    <button
                      key={`text-${color}`}
                      type="button"
                      className={styles.paletteSwatch}
                      style={{ backgroundColor: color }}
                      aria-label={color}
                      onClick={() => setRuleColor(idx, 'text', color)}
                      data-testid={`cf-rule-${idx}-text-swatch-${color}`}
                    />
                  ))}
                </div>
              ) : null}
              {openColorPicker &&
              openColorPicker.ruleIdx === idx &&
              openColorPicker.kind === 'bg' ? (
                <div
                  className={styles.palette}
                  role="group"
                  aria-label="Fill color palette"
                  data-testid={`cf-rule-${idx}-bg-color-palette`}
                >
                  {COLOR_PALETTE.map((color) => (
                    <button
                      key={`bg-${color}`}
                      type="button"
                      className={styles.paletteSwatch}
                      style={{ backgroundColor: color }}
                      aria-label={color}
                      onClick={() => setRuleColor(idx, 'bg', color)}
                      data-testid={`cf-rule-${idx}-bg-swatch-${color}`}
                    />
                  ))}
                </div>
              ) : null}

              {rule.groups.map((group, gIdx) => (
                <React.Fragment key={gIdx}>
                  {gIdx > 0 ? (
                    <div className={styles.orGroupSep}>OR</div>
                  ) : null}
                  <div
                    className={styles.andRow}
                    data-testid={`cf-rule-${idx}-group-${gIdx}`}
                  >
                    {group.map((cond, kIdx) => (
                      <React.Fragment key={kIdx}>
                        {kIdx > 0 ? <span>AND</span> : null}
                        <div className={styles.opSelectWrap}>
                          <ThemedSelect<CfOp>
                            value={cond.op}
                            options={OP_OPTIONS}
                            onChange={(op) =>
                              updateCondition(idx, gIdx, kIdx, { op })
                            }
                            ariaLabel={`Operator for group ${gIdx + 1} condition ${kIdx + 1}`}
                            testId={`cf-rule-${idx}-group-${gIdx}-cond-${kIdx}-op`}
                          />
                        </div>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${styles.targetInput} ${
                            parseTarget(cond.rawTarget) === null
                              ? styles.targetInputInvalid
                              : ''
                          }`}
                          value={cond.rawTarget}
                          placeholder="0"
                          onChange={(e) =>
                            updateCondition(idx, gIdx, kIdx, {
                              rawTarget: e.target.value,
                            })
                          }
                          aria-label={`Target for group ${gIdx + 1} condition ${kIdx + 1}`}
                          data-testid={`cf-rule-${idx}-group-${gIdx}-cond-${kIdx}-target`}
                        />
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => removeAndCondition(idx, gIdx, kIdx)}
                          aria-label="Remove condition"
                          data-testid={`cf-rule-${idx}-group-${gIdx}-cond-${kIdx}-delete`}
                        >
                          <X size={14} aria-hidden="true" />
                        </button>
                      </React.Fragment>
                    ))}
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => addAndCondition(idx, gIdx)}
                      disabled={
                        group.length >= MAX_CF_AND_CONDITIONS_PER_GROUP
                      }
                      data-testid={`cf-rule-${idx}-group-${gIdx}-add-and`}
                    >
                      + AND
                    </button>
                    {rule.groups.length > 1 ? (
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => removeOrGroup(idx, gIdx)}
                        aria-label={`Remove group ${gIdx + 1}`}
                        data-testid={`cf-rule-${idx}-group-${gIdx}-delete`}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </React.Fragment>
              ))}

              <button
                type="button"
                className={styles.linkButton}
                onClick={() => addOrGroup(idx)}
                disabled={rule.groups.length >= MAX_CF_OR_GROUPS_PER_RULE}
                data-testid={`cf-rule-${idx}-add-or-group`}
              >
                + OR group
              </button>

              {errorMessage ? (
                <div
                  className={styles.errorMsg}
                  data-testid={`cf-rule-${idx}-error`}
                >
                  {errorMessage}
                </div>
              ) : null}
            </div>
          );
        })}

        <button
          type="button"
          className={styles.secondaryButton}
          onClick={addRule}
          disabled={drafts.length >= MAX_CF_RULES_PER_CELL}
          data-testid="cf-add-rule"
        >
          <Plus size={15} aria-hidden="true" /> Add rule
        </button>
      </div>

      <div className={styles.footer}>
        <span className={styles.footerNote}>First matching rule wins.</span>
        <span className={styles.footerSpacer} />
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={onCancel}
          data-testid="cf-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleApply}
          disabled={!canApply}
          data-testid="cf-apply"
        >
          Apply
        </button>
      </div>
    </div>
  );
};
